import { createStart, createMiddleware } from "@tanstack/react-start";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

/**
 * Baseline security headers attached to every server response.
 *
 * - X-Frame-Options: DENY            — prevents clickjacking by disallowing
 *                                       the site from being framed.
 * - X-Content-Type-Options: nosniff  — stops browsers from MIME-sniffing
 *                                       responses away from the declared type.
 * - Referrer-Policy                  — limits how much referrer data leaks
 *                                       to third parties.
 * - Permissions-Policy               — disables sensors / device APIs we
 *                                       don't use.
 * - Strict-Transport-Security        — force HTTPS for a year on all
 *                                       subdomains (only meaningful on
 *                                       HTTPS responses; harmless otherwise).
 * - Content-Security-Policy          — broad allowlist tuned to what the
 *                                       app actually needs:
 *                                         • self for app assets
 *                                         • Supabase URL for API/auth
 *                                         • inline styles (Tailwind/shadcn)
 *                                         • data: images for inline SVGs
 *                                       'unsafe-inline' on style-src is
 *                                       required by the current styling
 *                                       approach. Scripts stay locked to
 *                                       'self' to block injected JS.
 */
const securityHeadersMiddleware = createMiddleware().server(async ({ next, request }) => {
  // Bypass /lovable/* and /email/unsubscribe (webhook + cron + one-click
  // unsubscribe routes that authenticate themselves).
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/") || url.pathname === "/email/unsubscribe") {
    return next();
  }

  const result = await next();

  // Pull the Supabase URL into the CSP connect-src list so the browser
  // is allowed to talk to the backend. Falls back to wildcard *.supabase.co
  // if the env isn't set at request time (it always should be).
  const supabaseUrl = process.env.SUPABASE_URL || "";
  let supabaseOrigin = "https://*.supabase.co";
  try {
    if (supabaseUrl) supabaseOrigin = new URL(supabaseUrl).origin;
  } catch {
    // ignore — fall back to wildcard
  }

  const csp = [
    "default-src 'self'",
    "base-uri 'self'",
    "object-src 'none'",
    "frame-ancestors 'none'",
    "form-action 'self'",
    "img-src 'self' data: blob: https:",
    "font-src 'self' data:",
    // Tailwind + shadcn rely on inline styles
    "style-src 'self' 'unsafe-inline'",
    // Scripts: self + 'unsafe-inline'. TanStack Start ships an inline
    // hydration bootstrap script (the $tsr stream barrier) that is required
    // for the React app to hydrate. Without 'unsafe-inline' the browser
    // blocks it and the page never becomes interactive — forms appear
    // "dead" because no event handlers are attached.
    // challenges.cloudflare.com — Cloudflare Turnstile widget script.
    // us-assets.i.posthog.com — analytics config bootstrap loaded by posthog-js.
    "script-src 'self' 'unsafe-inline' https://challenges.cloudflare.com https://cdn.gpteng.co https://us-assets.i.posthog.com",
    // Turnstile renders its challenge inside an iframe from this origin.
    "frame-src https://challenges.cloudflare.com",
    `connect-src 'self' ${supabaseOrigin} https://*.lovable.app wss://*.supabase.co https://challenges.cloudflare.com https://us-assets.i.posthog.com https://us.i.posthog.com`,
  ].join("; ");

  const response = result.response;
  const headers = new Headers(response.headers);
  headers.set("X-Frame-Options", "DENY");
  headers.set("X-Content-Type-Options", "nosniff");
  headers.set("Referrer-Policy", "strict-origin-when-cross-origin");
  headers.set("Permissions-Policy", "camera=(), microphone=(), geolocation=(), interest-cohort=()");
  headers.set("Strict-Transport-Security", "max-age=31536000; includeSubDomains");
  headers.set("Content-Security-Policy", csp);

  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [securityHeadersMiddleware],
}));
